import { Capacitor } from '@capacitor/core';

/** `android_app` = the Capacitor Android wrapper (the only native build); everything else is `web`. */
export type DeviceKind = 'android_app' | 'web';

/** Friendly description of a device, sent with sign-in so the manager can recognise it. */
export type DeviceDescription = {
  /** e.g. "Android app · SM-A546E", "iPhone · Safari", "Windows · Edge" (max 64 chars). */
  name: string;
  type: DeviceKind;
  /** e.g. "Android 14", "iOS 17.6", "Windows", "macOS"; empty when unknown. */
  os: string;
  /** e.g. "Chrome WebView 129", "Safari 17.6", "Edge 129"; empty when unknown. */
  browser: string;
};

export const DEVICE_NAME_MAX_LENGTH = 64;

type Platform = 'iPhone' | 'iPad' | 'Android' | 'Windows' | 'Chromebook' | 'Mac' | 'Linux';

type BrowserMatch = { name: string; version: string | undefined };

/** Android System WebView marks its user agent with "; wv)". */
const ANDROID_WEBVIEW_RE = /;\s*wv\)/;

/** First match wins: Edge, Samsung and Opera also contain "Chrome"; almost everything contains "Safari". */
const BROWSERS: ReadonlyArray<{ re: RegExp; name: string }> = [
  { re: /Edg(?:e|A|iOS)?\/(\d+)/, name: 'Edge' },
  { re: /SamsungBrowser\/(\d+(?:\.\d+)?)/, name: 'Samsung Internet' },
  { re: /(?:OPR|OPT)\/(\d+)/, name: 'Opera' },
  { re: /(?:Firefox|FxiOS)\/(\d+)/, name: 'Firefox' },
  { re: /(?:CriOS|Chrome)\/(\d+)/, name: 'Chrome' },
  { re: /Version\/(\d+(?:\.\d+)?).*Safari\//, name: 'Safari' },
];

function clamp(value: string): string {
  return value.slice(0, DEVICE_NAME_MAX_LENGTH).trim();
}

function detectPlatform(ua: string): Platform | null {
  if (/iPad/.test(ua)) return 'iPad';
  if (/iPhone|iPod/.test(ua)) return 'iPhone';
  if (/Android/.test(ua)) return 'Android';
  if (/Windows/.test(ua)) return 'Windows';
  if (/CrOS/.test(ua)) return 'Chromebook';
  if (/Macintosh|Mac OS X/.test(ua)) return 'Mac';
  if (/Linux|X11/.test(ua)) return 'Linux';
  return null;
}

function detectOs(ua: string, platform: Platform | null): string {
  switch (platform) {
    case 'iPhone':
    case 'iPad': {
      const m = /OS (\d+(?:_\d+)*) like Mac OS X/.exec(ua);
      return m ? `iOS ${m[1].replace(/_/g, '.')}` : 'iOS';
    }
    case 'Android': {
      const m = /Android (\d+(?:\.\d+)*)/.exec(ua);
      return m ? `Android ${m[1]}` : 'Android';
    }
    case 'Windows':
      return 'Windows';
    case 'Chromebook':
      return 'ChromeOS';
    case 'Mac':
      return 'macOS';
    case 'Linux':
      return 'Linux';
    default:
      return '';
  }
}

function detectBrowser(ua: string, isApp: boolean): BrowserMatch | null {
  if (isApp) {
    const m = /Chrome\/(\d+)/.exec(ua);
    return { name: 'Chrome WebView', version: m ? m[1] : undefined };
  }
  for (const b of BROWSERS) {
    const m = b.re.exec(ua);
    if (m) return { name: b.name, version: m[1] };
  }
  return null;
}

/** Model from "Android 14; SM-A546E Build/UP1A…". Chrome's reduced UA reports "K" instead of a model. */
function androidModel(ua: string): string | null {
  const m = /Android [^;)]*;\s*([^;)]+?)(?:\s+Build\/[^;)]*)?\s*[;)]/.exec(ua);
  if (!m) return null;
  const model = m[1].trim();
  if (!model || model === 'K' || model.toLowerCase() === 'wv') return null;
  return model;
}

/**
 * Pure: describe a device from its user agent. `isNativeApp` is true inside the Capacitor wrapper;
 * an Android WebView user agent ("; wv)") is treated as the app too.
 */
export function describeDevice(userAgent: string, isNativeApp: boolean): DeviceDescription {
  const ua = (userAgent || '').trim();
  const platform = detectPlatform(ua);
  const isApp = isNativeApp || ANDROID_WEBVIEW_RE.test(ua);
  const os = detectOs(ua, platform);
  const browser = detectBrowser(ua, isApp);
  const browserLabel = browser ? [browser.name, browser.version].filter(Boolean).join(' ') : '';

  if (isApp) {
    const model = androidModel(ua);
    return {
      name: clamp(model ? `Android app · ${model}` : 'Android app'),
      type: 'android_app',
      os: clamp(os),
      browser: clamp(browserLabel),
    };
  }

  let name: string;
  if (platform) {
    name = browser ? `${platform} · ${browser.name}` : platform;
  } else {
    name = browser ? `Web browser · ${browser.name}` : 'Web browser';
  }
  return { name: clamp(name), type: 'web', os: clamp(os), browser: clamp(browserLabel) };
}

function isCapacitorNative(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/** Describe the device this page runs on (Capacitor native check + navigator.userAgent). */
export function describeThisDevice(): DeviceDescription {
  return describeDevice(navigator.userAgent || '', isCapacitorNative());
}
